import { type JustRan, bannerView } from '@/lib/atelier-status';
import { DISABLED_INERT } from '@/lib/disabled-inert';
import {
  PROD_JUDGMENT_KEY,
  PROD_STATE_KEY,
  SYNC_STATUS_KEY,
  runSync,
  useSyncStatus,
} from '@/lib/promotion-api';
import { useInstanceContext, useIsVitrine } from '@/lib/use-instance';
import { useNow } from '@/lib/use-now';
import { LoadingRing } from '@pms-digital/design-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * The banner of the ATELIER (docs/specs/vitrine.md §9.3, issue #192, lot F; maquette `atelier.html`
 * section 1, variant A retained by the review of 2026-10-14): one thin line above the whole
 * administration, the rail included. It says the code the prod runs, the code of the dev with a link
 * to the advance, how the last synchronisation went, carries « Synchroniser », and leads to
 * « État de la prod ». On a failure it turns red and says when it failed and when it last succeeded.
 *
 * It is in the normal flow and NOT sticky, on purpose: every sticky surface of the admin is pinned to
 * `--spacing-admin-bar` from the top of the window, and a second fixed furniture above them would move
 * ten surfaces for a line that is only read at the top of a page.
 *
 * Drawn on the atelier ONLY and only where a prod is linked (`configured`): without `PROD_ORIGIN`
 * there is nothing to say, and the vitrine has no such route. The state is read again every minute
 * (`useSyncStatus`), and at the end of a click.
 *
 * Everything it SAYS comes from `lib/atelier-status.ts`, pure and tested; this file draws it.
 */

export function AtelierBanner() {
  const vitrine = useIsVitrine();
  const { code } = useInstanceContext();
  const queryClient = useQueryClient();
  const status = useSyncStatus(!vitrine);
  const now = useNow();
  const [justRan, setJustRan] = useState<JustRan | null>(null);

  const run = useMutation({
    mutationFn: runSync,
    onSuccess: (result) => {
      // The answer IS the state after the run: written at once, then read again with the screens
      // that depend on it - the pointers the prod realigned change what it will say of a line.
      queryClient.setQueryData(SYNC_STATUS_KEY, result.status);
      setJustRan({ summary: result.report.summary, at: Date.now() });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY });
      void queryClient.invalidateQueries({ queryKey: PROD_STATE_KEY });
      void queryClient.invalidateQueries({ queryKey: [PROD_JUDGMENT_KEY] });
    },
  });

  if (vitrine || status.data === undefined || !status.data.configured) return null;

  // A click waits for the run: the banner is « en cours » for the whole of it, even before the poll sees it.
  const view = bannerView({
    status: { ...status.data, running: status.data.running || run.isPending },
    devCode: code,
    now,
    justRan,
  });

  return (
    <div
      data-testid="atelier-bandeau"
      data-etat={view.failed ? 'echec' : 'ok'}
      className={`flex flex-wrap items-center gap-x-3.5 gap-y-0.5 px-3.5 py-1 text-caption leading-[18px] ${
        view.failed ? 'bg-rouge-tint text-rouge-deep' : 'bg-bleu-ciel-tint text-bleu-ciel-deep'
      }`}
    >
      <span className="font-bold uppercase tracking-[0.06em]">Atelier</span>
      <span data-testid="atelier-bandeau-prod">{view.prodText}</span>
      <span data-testid="atelier-bandeau-dev">
        {view.devText}
        {view.compareHref === null ? null : (
          <>
            {' '}
            <a
              data-testid="atelier-bandeau-avance"
              href={view.compareHref}
              target="_blank"
              rel="noreferrer noopener"
              className="font-semibold underline underline-offset-2"
            >
              voir l’avance
            </a>
          </>
        )}
      </span>
      <span data-testid="atelier-bandeau-etat" className="font-semibold">
        {view.running ? (
          <span aria-hidden="true" className="mr-1 inline-flex align-[-1px]">
            <LoadingRing size="xs" />
          </span>
        ) : null}
        {view.stateText}
      </span>
      <button
        type="button"
        data-testid="atelier-synchroniser"
        disabled={view.running}
        onClick={() => run.mutate()}
        className={`cursor-pointer rounded-md border border-current px-2 font-semibold leading-[18px] disabled:cursor-not-allowed ${DISABLED_INERT}`}
      >
        Synchroniser
      </button>
      <Link
        to="/admin/prod"
        data-testid="atelier-etat-prod-lien"
        className="ml-auto font-semibold text-inherit underline-offset-2 hover:underline"
      >
        État de la prod ›
      </Link>
    </div>
  );
}
